import { isAxiosError } from 'axios';

import { hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import {
  ContentStatus,
  JourneyHub,
  JourneySummaryHub,
  SaveJourneyHubRequest,
} from '@addmile/backend-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toCopyRequest, toJourneyDraft, toSaveRequest } from '../draft';

export const journeyKeys = {
  all: ['journeys'] as const,
  list: () => [...journeyKeys.all, 'list'] as const,
  journey: (id: string) => [...journeyKeys.all, id] as const,
};

// The backend explains a rejected save (a locked structure, a missing block) in the body's error.
export const journeyErrorMessage = (error: unknown, fallback: string) => {
  if (isAxiosError(error) && typeof error.response?.data?.error === 'string') {
    return error.response.data.error;
  }

  return error instanceof Error ? error.message : fallback;
};

export class JourneyService {
  // Journeys are few, so the list loads them all and filters by status on the page, which also
  // gives the status filter its counts.
  static async getJourneys(): Promise<JourneySummaryHub[]> {
    const token = await getAuthToken();
    const { data } = await hubApi.listJourneysHub(
      undefined,
      undefined,
      undefined,
      withAuthHeader(token),
    );
    return data.journeys;
  }

  static async getJourney(id: string): Promise<JourneyHub> {
    const token = await getAuthToken();
    const { data } = await hubApi.getJourneyHub(id, withAuthHeader(token));
    return data;
  }

  static async createJourney(journey: SaveJourneyHubRequest): Promise<JourneyHub> {
    const token = await getAuthToken();
    const { data } = await hubApi.createJourneyHub(journey, withAuthHeader(token));
    return data;
  }

  static async updateJourney(id: string, journey: SaveJourneyHubRequest): Promise<JourneyHub> {
    const token = await getAuthToken();
    const { data } = await hubApi.updateJourneyHub(id, journey, withAuthHeader(token));
    return data;
  }

  static async deleteJourney(id: string): Promise<void> {
    const token = await getAuthToken();
    await hubApi.deleteJourneyHub(id, withAuthHeader(token));
  }

  // Going online is refused while anything is left to fix, so save the content under the current
  // status first: it is never lost, and its fresh issues decide whether the status can follow.
  static async saveJourney(
    id: string | undefined,
    journey: SaveJourneyHubRequest,
    savedStatus: ContentStatus = ContentStatus.Draft,
  ): Promise<JourneyHub> {
    const publishing =
      journey.status === ContentStatus.Online && savedStatus !== ContentStatus.Online;
    const firstPass = publishing ? { ...journey, status: savedStatus } : journey;

    const saved = id
      ? await JourneyService.updateJourney(id, firstPass)
      : await JourneyService.createJourney(firstPass);

    if (!publishing || saved.issues.length > 0) {
      return saved;
    }

    // From saved, so nodes the first pass created keep their new ids instead of being recreated.
    return JourneyService.updateJourney(saved.id, {
      ...toSaveRequest(toJourneyDraft(saved)),
      status: journey.status,
    });
  }
}

export const useJourneys = () =>
  useQuery({
    queryKey: journeyKeys.list(),
    queryFn: () => JourneyService.getJourneys(),
    staleTime: 2 * 60 * 1000,
  });

export const useJourney = (id?: string) =>
  useQuery({
    queryKey: journeyKeys.journey(id ?? ''),
    queryFn: () => JourneyService.getJourney(id as string),
    enabled: !!id,
    // The editor opens on a fresh fetch, and nothing refetches behind it: a failed background
    // refetch would swap the editor for an error and lose the draft.
    refetchOnMount: 'always',
    refetchOnReconnect: false,
  });

export const useSaveJourney = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      journey,
      savedStatus,
    }: {
      id?: string;
      journey: SaveJourneyHubRequest;
      savedStatus?: ContentStatus;
    }) => JourneyService.saveJourney(id, journey, savedStatus),
    onSuccess: (saved) => {
      queryClient.setQueryData(journeyKeys.journey(saved.id), saved);
      queryClient.invalidateQueries({ queryKey: journeyKeys.list() });
    },
  });
};

// The API has no status-only update, so the journey is saved whole with the new status.
export const useChangeJourneyStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ContentStatus }) => {
      const journey = await JourneyService.getJourney(id);
      const request = { ...toSaveRequest(toJourneyDraft(journey)), status };

      return JourneyService.saveJourney(id, request, journey.status);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(journeyKeys.journey(saved.id), saved);
      queryClient.invalidateQueries({ queryKey: journeyKeys.list() });
    },
  });
};

export const useDuplicateJourney = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const journey = await JourneyService.getJourney(id);

      return JourneyService.createJourney(toCopyRequest(journey));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.list() });
    },
  });
};

export const useDeleteJourney = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => JourneyService.deleteJourney(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: journeyKeys.journey(id) });
      queryClient.invalidateQueries({ queryKey: journeyKeys.list() });
    },
  });
};
